import {
  type AdminProvisionTestJobSuccess,
  type PostApiAdminProvisionTestRequestBody,
  useGetApiAdminProvisionTestJob,
  usePostApiAdminProvisionTest,
} from '@sit/client-shared';
import { skipToken } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 2_000;

/**
 * Schedules a provision-test job and polls GET /admin/provision-test/jobs/{jobId}
 * until terminal.
 */
export function useProvisionTestJob() {
  const [jobId, setJobId] = useState<string | undefined>();
  const [result, setResult] = useState<
    AdminProvisionTestJobSuccess | undefined
  >();
  const [error, setError] = useState<Error | null>(null);
  const onSuccessRef = useRef<
    ((data: AdminProvisionTestJobSuccess) => void | Promise<void>) | undefined
  >(undefined);

  const { mutate, isPending: isScheduling } = usePostApiAdminProvisionTest({
    onError: (err) => {
      setError(err instanceof Error ? err : new Error(String(err)));
      setJobId(undefined);
    },
  });

  const { data: jobStatus, error: jobStatusError } =
    useGetApiAdminProvisionTestJob(
      jobId
        ? {
            pathParams: { jobId },
            requestConfig: {
              skipToast: true,
            },
          }
        : skipToken,
      {
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: jobId ? POLL_INTERVAL_MS : false,
        retry: (failureCount, err) => {
          if (!(err instanceof Error)) {
            return false;
          }
          if (err.message?.includes('Too many requests') || failureCount < 5) {
            return true;
          }
          return false;
        },
      },
    );

  useEffect(() => {
    if (!jobId || !jobStatus) {
      return;
    }

    if (jobStatus.state === 'completed') {
      const jobResult = jobStatus.result;
      setJobId(undefined);
      if (jobResult == null) {
        setError(new Error('Provision test completed without a result'));
        return;
      }
      if (!jobResult.ok) {
        setError(new Error(jobResult.message));
        setResult(undefined);
        return;
      }
      setError(null);
      setResult(jobResult);
      void onSuccessRef.current?.(jobResult);
      return;
    }

    if (jobStatus.state === 'failed') {
      setJobId(undefined);
      setResult(undefined);
      setError(
        new Error(jobStatus.failedReason ?? 'Provision test job failed'),
      );
    }
  }, [jobId, jobStatus]);

  useEffect(() => {
    if (!jobStatusError || !jobId) {
      return;
    }
    if (jobStatusError.status === 404) {
      setJobId(undefined);
      setError(new Error('Provision test job expired before result was read'));
    }
  }, [jobId, jobStatusError]);

  const run = useCallback(
    (
      body: PostApiAdminProvisionTestRequestBody,
      options?: {
        onSuccess?: (
          data: AdminProvisionTestJobSuccess,
        ) => void | Promise<void>;
      },
    ) => {
      setError(null);
      setResult(undefined);
      onSuccessRef.current = options?.onSuccess;
      mutate(
        { body },
        {
          onSuccess: ({ jobId: scheduledJobId }) => {
            setJobId(scheduledJobId);
          },
        },
      );
    },
    [mutate],
  );

  return {
    run,
    result,
    error,
    isPending: isScheduling || !!jobId,
    jobId,
  };
}
